import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, getSession, getCurrentUser } from '../lib/supabase';
import type { User } from '../types/database.types';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  error: string | null;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadUser() {
      try {
        // Check for existing session
        const { data: sessionData, error: sessionError } = await getSession();

        if (!mounted) return;

        if (sessionError) {
          console.error('Session error:', sessionError);
          setUser(null);
          setLoading(false);
          return;
        }

        const session = sessionData.session;

        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
          });
        } else {
          setUser(null);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error loading user:', err);
        if (mounted) {
          setError('Failed to load user data');
          setUser(null);
          setLoading(false);
        }
      }
    }

    loadUser();

    // Set up auth subscription
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
          });
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};